import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { apiErrorMessage } from "@/lib/api";
import { productTypeLabel, sectionBucket } from "@/lib/production";
import { Button } from "@/components/ui/button";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function ProductionCalendar() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useResource("/production-plans");
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(dayjs());

  const plans = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const byDay = useMemo(() => {
    const m = {};
    plans.forEach((p) => { const k = dayjs(p.date).format("YYYY-MM-DD"); (m[k] = m[k] || []).push(p); });
    return m;
  }, [plans]);

  const days = useMemo(() => {
    if (view === "week") {
      const start = cursor.startOf("week");
      return Array.from({ length: 7 }).map((_, i) => start.add(i, "day"));
    }
    const start = cursor.startOf("month").startOf("week");
    return Array.from({ length: 42 }).map((_, i) => start.add(i, "day"));
  }, [cursor, view]);

  const label = view === "week"
    ? `${cursor.startOf("week").format("DD MMM")} – ${cursor.endOf("week").format("DD MMM YYYY")}`
    : cursor.format("MMMM YYYY");
  const step = (dir) => setCursor((c) => c[dir > 0 ? "add" : "subtract"](1, view === "week" ? "week" : "month"));

  const openPlan = (p) => navigate(`/production-plans/${p.id}`);

  return (
    <div className="space-y-6">
      <PageHeader title="Production Calendar" subtitle="Planned production across the month or week. Click a plan to open it.">
        <div className="flex items-center gap-2">
          <div className="mr-1 flex rounded-md border border-stone-200 p-0.5">
            {["week", "month"].map((v) => (
              <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)}
                className={`rounded px-2.5 py-1 text-xs font-medium capitalize ${view === v ? "bg-amber-700 text-white" : "text-stone-600"}`}>{v}</button>
            ))}
          </div>
          <Button data-testid="cal-prev" variant="outline" size="icon" className="h-9 w-9" onClick={() => step(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="w-44 text-center font-display text-sm font-semibold text-stone-800">{label}</span>
          <Button data-testid="cal-next" variant="outline" size="icon" className="h-9 w-9" onClick={() => step(1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </PageHeader>

      {isLoading ? <LoadingState label="Loading plans…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> :
        plans.length === 0 ? <EmptyState title="No production plans" message="Plans created on web or mobile will show on the calendar." /> : (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            <div className="grid grid-cols-7 border-b border-stone-200 bg-stone-50 text-center text-[11px] font-bold uppercase tracking-wider text-stone-500">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {days.map((day) => {
                const key = day.format("YYYY-MM-DD");
                const inMonth = view === "week" || day.month() === cursor.month();
                const items = byDay[key] || [];
                const isToday = day.isSame(dayjs(), "day");
                return (
                  <div key={key} data-testid={`cal-day-${key}`} className={`${view === "week" ? "min-h-[220px]" : "min-h-[104px]"} border-b border-r border-stone-100 p-1.5 ${inMonth ? "bg-white" : "bg-stone-50/50"}`}>
                    <div className={`mb-1 text-right text-xs ${isToday ? "font-bold text-amber-700" : inMonth ? "text-stone-600" : "text-stone-300"}`}>{day.date()}</div>
                    <div className="space-y-1">
                      {items.slice(0, view === "week" ? 8 : 3).map((p) => (
                        <button key={p.id} data-testid={`cal-plan-${p.id}`} onClick={() => openPlan(p)}
                          className={`flex w-full items-center justify-between gap-1 rounded px-1.5 py-1 text-left text-[10px] font-medium hover:opacity-80 ${sectionBucket(p.section) === "nonbakery" ? "bg-sky-50 text-sky-900" : "bg-amber-50 text-amber-900"}`}>
                          <span className="truncate">{productTypeLabel(p.section) || p.section}{(p.lines || []).length ? ` · ${(p.lines || []).length}` : ""}</span>
                          <StatusBadge value={p.status} className="scale-75" />
                        </button>
                      ))}
                      {items.length > (view === "week" ? 8 : 3) && <div className="text-[10px] text-muted-foreground">+{items.length - (view === "week" ? 8 : 3)} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
    </div>
  );
}
